import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuRounded from "@mui/icons-material/MenuRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { getPrimaryColor } from "../GlobalFunction/Theme";

const FONT = {
  family:
    '"LINE Seed TW", "LINE Seed JP", "LINE Seed Sans", "Noto Sans TC",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue",Arial,"PingFang TC","Microsoft JhengHei",sans-serif',
  weightText: 400,
  weightHead: 700,
  size: { body: 14, h1: 28, h2: 22, h3: 18, side: 15 },
  lineHeight: { body: 1.9, head: 1.35 },
};

export default function PrivacyTermsPage({
  // navbar height
  topOffset = 72,
  mode,
  bodyFontSize = FONT.size.body,
}) {
  const SECTIONS = useMemo(
    () => [
      { id: "intro", title: "前言" },
      { id: "s1", title: "重要資訊及本公司" },
      { id: "s2", title: "我們所蒐集的資料" },
      { id: "s3", title: "如何蒐集您的個人資料" },
      { id: "s4", title: "如何利用您的個人資料" },
      { id: "s5", title: "個人資料的揭露" },
      { id: "s6", title: "國際傳輸" },
      { id: "s7", title: "資料安全" },
      { id: "s8", title: "資料保存" },
      { id: "s9", title: "您的法律權利" },
      { id: "s10", title: "使用本活動之錄音/錄影和照片" },
    ],
    []
  );

  const primary = getPrimaryColor(mode);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [openDrawer, setOpenDrawer] = useState(false);
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  const STICKY_GAP = 25;
  const sectionPositionsRef = useRef([]);

  const numbered = (id, title) => {
    const idx = SECTIONS.findIndex((s) => s.id === id);
    return idx <= 0 ? title : `${idx}. ${title}`;
  };

  // Calculate section positions + scroll spy
  useEffect(() => {
    const calcPositions = () => {
      sectionPositionsRef.current = SECTIONS.map((s) => {
        const el = document.getElementById(s.id);
        if (!el) return null;
        const top = el.getBoundingClientRect().top + window.scrollY;
        return { id: s.id, top };
      }).filter(Boolean);
    };

    const onScroll = () => {
      if (!sectionPositionsRef.current.length) return;
      const y = window.scrollY + topOffset + STICKY_GAP + 10; // active line
      let current = SECTIONS[0]?.id;
      for (const sec of sectionPositionsRef.current) {
        if (y >= sec.top) {
          current = sec.id;
        } else {
          break;
        }
      }
      setActiveId((prev) => (prev === current ? prev : current));
    };

    calcPositions();
    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", calcPositions);
    window.addEventListener("load", calcPositions);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", calcPositions);
      window.removeEventListener("load", calcPositions);
    };
  }, [SECTIONS, topOffset, STICKY_GAP]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - topOffset - 8;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  // ---------- Sidebar list ----------
  const SideList = (
    <Box
      sx={{
        width: { xs: "100vw", md: 300 },
        maxHeight: { xs: `calc(100dvh - ${topOffset}px)`, md: "unset" },
        overflowY: { xs: "auto", md: "visible" },
        overflowX: { xs: "auto", md: "hidden" },
        bgcolor: { xs: "background.paper", md: "transparent" },
      }}
    >
      {/* Mobile drawer header */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          position: "sticky",
          top: 0,
          bgcolor: "background.paper",
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT.family,
            fontWeight: FONT.weightHead,
            fontSize: FONT.size.h3,
            lineHeight: FONT.lineHeight.head,
          }}
        >
          目錄
        </Typography>
        <IconButton onClick={() => setOpenDrawer(false)} aria-label="close">
          <CloseRounded />
        </IconButton>
      </Box>

      <List sx={{ py: 1 }}>
        {SECTIONS.map((s) => {
          const active = s.id === activeId;
          return (
            <ListItemButton
              key={s.id}
              onClick={() => {
                scrollTo(s.id);
                if (isMobile) setOpenDrawer(false);
              }}
              sx={{
                alignItems: "flex-start",
                py: 1,
                px: 2,
                borderLeft: {
                  md: active ? `3px solid ${primary}` : "3px solid transparent",
                },
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <ListItemText
                primary={numbered(s.id, s.title)}
                primaryTypographyProps={{
                  sx: {
                    fontFamily: FONT.family,
                    fontWeight: active ? FONT.weightHead : FONT.weightText,
                    fontSize: FONT.size.side,
                    lineHeight: 1.5,
                    color: active ? "text.primary" : "text.secondary",
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  // ---------- Helpers ----------
  const Section = ({ id, title, children }) => (
    <Box
      id={id}
      sx={{
        scrollMarginTop: topOffset + STICKY_GAP + 12,
        mb: { xs: 5, md: 7 },
      }}
    >
      <Typography
        component="h2"
        sx={{
          fontFamily: FONT.family,
          fontWeight: FONT.weightHead,
          fontSize: { xs: FONT.size.h2, md: FONT.size.h1 },
          lineHeight: FONT.lineHeight.head,
          mb: 1.5,
        }}
      >
        {numbered(id, title)}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Typography
        component="div"
        sx={{
          fontFamily: FONT.family,
          fontWeight: FONT.weightText,
          fontSize: bodyFontSize,
          lineHeight: FONT.lineHeight.body,
          color: "text.primary",
          "& p": {
            mb: 1.5,
            fontFamily: FONT.family,
            fontWeight: FONT.weightText,
            fontSize: bodyFontSize,
            lineHeight: FONT.lineHeight.body,
          },
          "& ul": {
            pl: 3,
            mb: 1.5,
            fontFamily: FONT.family,
            fontWeight: FONT.weightText,
            fontSize: bodyFontSize,
            lineHeight: FONT.lineHeight.body,
          },
          "& ol": {
            pl: 3,
            mb: 1.5,
            fontFamily: FONT.family,
            fontWeight: FONT.weightText,
            fontSize: bodyFontSize,
            lineHeight: FONT.lineHeight.body,
          },
        }}
      >
        {children}
      </Typography>
    </Box>
  );

  const SimpleTable = ({ head, rows }) => (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ mb: 3, borderRadius: 1.5 }}
    >
      <Table
        size="small"
        sx={{
          "& th, & td": { fontFamily: FONT.family, fontSize: 14 },
        }}
      >
        <TableHead>
          <TableRow>
            {head.map((h) => (
              <TableCell key={h} sx={{ fontWeight: FONT.weightHead }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              {r.map((c, j) => (
                <TableCell key={j}>{c}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ---------- Page ----------
  return (
    <Box
      sx={{
        fontFamily: FONT.family,
        color: "text.primary",
        mt: `${topOffset}px`,
        maxWidth: "1200px",
        mx: "auto",
        px: { xs: 2, md: 3 },
        pb: { xs: 6, md: 10 },
      }}
    >
      {/* Mobile header */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <IconButton
          onClick={() => setOpenDrawer(true)}
          aria-label="open navigation"
        >
          <MenuRounded />
        </IconButton>
        <Typography
          sx={{
            fontFamily: FONT.family,
            fontWeight: FONT.weightHead,
            fontSize: FONT.size.h2,
            lineHeight: FONT.lineHeight.head,
          }}
        >
          隱私權及網站使用條款
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
          gap: { xs: 0, md: 4 },
        }}
      >
        {/* Desktop sidebar */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "sticky",
            top: topOffset + STICKY_GAP,
            alignSelf: "start",
            height: `calc(100dvh - ${topOffset + STICKY_GAP + 24}px)`,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Paper
            variant="outlined"
            sx={{ p: 1, borderRadius: 2, overflow: "hidden" }}
          >
            {SideList}
          </Paper>
        </Box>

        {/* Content */}
        <Box sx={{ pt: { xs: 1, md: 0 } }}>
          {/* Desktop title */}
          <Typography
            sx={{
              display: { xs: "none", md: "block" },
              fontFamily: FONT.family,
              fontWeight: FONT.weightHead,
              fontSize: FONT.size.h1,
              lineHeight: FONT.lineHeight.head,
              mb: 3,
            }}
          >
            隱私權及網站使用條款
          </Typography>
          <p>
            隱私權規範及聲明，關於個人資料保護，請用戶或會員詳細閱讀以下（台灣）
          </p>

          {/* ---- 前言 ---- */}
          <Section id="intro" title="前言">
            <p>
              台灣水禾實業有限公司以下簡稱「本公司」或「SEHO」
              ）尊重您的隱私，並致力於保護您的個人資料（定義如第 2 段所示）。
              本隱私權規範及聲明謹以下列規範、聲明，向您說明本公司如何處理您在本公司的應用程式「SEHO
              官網」、「SEHO 官方購物網站」、 行動裝置 APP
              等應用程式（以下合稱「本應用程式」）註冊成為用戶、申請加入會員，或存取及∕或使用本公司的服務時，所蒐集的個人資料之目的、類別、利用範圍及方式，
              並說明您的隱私權、得行使之權利等事項，以及法律如何保護您。如您對本公司的隱私權聲明有問題或疑慮、請您告知與聯絡，本公司將盡快回覆說明。
            </p>
          </Section>

          {/* ---- 1 ---- */}
          <Section id="s1" title="重要資訊及本公司">
            <p>
              <b>本隱私權規範及聲明之目的</b> <br />
              本隱私聲明旨在向您提供有關我們如何透過您使用本應用程式而蒐集、處理和利用您個人資料的資訊。該個人資料包含在您註冊本應用程式、使用本應用程式或參與
              SEHO的活動（下稱「本活動」）時，依現行及後續修訂之個人資料保護法及其施行細則（下統稱為「個資法」）透過本應用程式提供的任何資料。
            </p>
            <p>
              本應用程式不予未經法定代理人許可之未成年人使用。
              如果您未成年，您應得法定代理人之允許。如果您按下「我同意」之按鈕，就表示您已取得法定代理人的之許可。
            </p>
            <p>
              當我們蒐集、處理或利用您的個人資料時，請務必閱讀本隱私權規範及聲明和我們可能在特定場合提供的任何其他通知或公告，
              以便完全了解我們使用您的資料的方式與理由。本隱私權規範及聲明是針對其他通知的補充，其目的並非取代其他通知。
              <br />
              本隱私權規範及聲明是針對其他通知的補充，其目的並非取代其他通知。
            </p>
            <p>
              <b>適用範圍</b>
              <br />
              本隱私權規範及聲明由本公司所發布。當本公司在隱私權規範及聲明中提及「SEHO」或「我們」
              、「本公司」時，即指負責蒐集、處理和利用您個人資料之台灣水禾實業有限公司。
            </p>
            <p>
              本公司隱私權規範及聲明以及其他所包含之告知事項，適用於本公司所擁有及經營的網站（包含但不限於「本應用程式」
              ） 。
              本應用程式內，可能包含許多連結、或其他合作夥伴及個別開店店家（包含但不限於其他網路商城、網路購物中心等）所提供的商品或服務，
              關於該等連結網站、合作夥伴網站或個別開店店家之網路店家的隱私權聲明及與個人資料保護有關之告知事項，請參閱各該連結。
            </p>
            <p>
              <b>聯絡資訊</b>
              <br />
              如果您對本隱私權規範及聲明有任何疑問，包含請求行使您的合法權利時，請依下列資訊與我們聯絡。
            </p>
            <p>客服信箱：service@sehopro.com</p>
            <p>客服專線：02-2609-6867</p>
            <p>
              客服服務時間：週一～週五
              9:00-12:00/13:00-18:00（國定假日或例假日除外）
            </p>
            <p>
              <b>本隱私權規範及聲明的變更修訂權利與效力及通知變更修訂的請求</b>
              <br />
              本公司有權且會隨時更新本隱私權規範及聲明，並得於修訂後公佈在本應用程式，不另行個別通知。本公司可採取之方式可能會透過公告、或其他可得知悉之方式（包含但不限於應用程式之通知型訊息）
              ，顯示修訂或變更於本應用程式上，作為通知您任何關於本隱私權規範及聲明的變更修訂。您同意更新的本隱私權規範及聲明條款仍對您發生效力，因此，您有義務亦可隨時在本應用程式s上詳閱最新版本的隱私權規範及聲明等相關告知事項。
            </p>
            <p>
              此外，如法律上有必要，我們會請您表示同意。有關本隱私權規範及聲明變更的通知，會顯示在本應用程式上。惟，若您關閉本應用程式服務通知時，將可能遺漏收受權益變更通知。
            </p>
            <p>
              讓我們保有您正確且符合現況的個人資料是非常重要的。如果您的個人資料在您與我們的關係存續中有所變動（包含但不限於變更手機號碼或變更綁定服務帳號者），請隨時通知我們。我們對因您所提供的個人資料不正確或過時所致任何損害概不負責。
            </p>
            <p>
              <b>第三方連結</b>
              <br />
              本應用程式可能含有引導至第三方網站（包含如電潔水、擦拭君、MIZUKA等外部網站）、外掛程式和應用程式的連結。點擊這些連結或啟用這些鏈接可能會允許第三人蒐集或共享您的資料。我們並未控制這些第三方網站或連結，也不對其隱私聲明或相關內容負責。當您離開本應用程式並瀏覽這些網站時，我們鼓勵您閱讀您瀏覽的每個網站的隱私權通知及內容。
            </p>
          </Section>

          {/* ---- 2 ---- */}
          <Section id="s2" title="我們所蒐集的資料">
            <p>
              <b>
                我們可能會蒐集、處理、利用、儲存及傳輸您不同類型的個人資料，我們將其分類如下（下統稱為「個人資料」）：
              </b>
              <br />
            </p>
            <ol type="A">
              <li>
                身份資料：包含本應用程式的帳號
                ID（唯一裝置識別碼）、電子郵件地址、電話號碼、個人相片、用戶名稱（暱稱）或類似的識別碼、出生年月日和性別、語音和聲音、含有您的影片及相片。
              </li>
              <li>
                聯絡資料：包含您提供給我們的姓名、電子郵件地址、電話號碼及/或郵政地址。
              </li>
              <li>
                交易資料：包含您的喜好、購買記錄、會員紅利點數、位置、商店登記歷史、已授予的優惠券，以及您在申請我們舉辦的本活動時，於本應用程式之申請表上輸入的資料，如您的姓名、電子郵件地址、電話號碼、參與者人數，以及本活動摘要。
              </li>
              <li>
                技術資料：包含網際網路通訊協議（IP）地址、您的帳戶登入資料、本應用程式記錄、裝置與瀏覽器類型與版本、時區設定與位置、相連之帳戶資訊（Facebook、Apple
                和 Google）
                、瀏覽器外掛程式之類型與版本、作業系統與平台，以及您用來存取本應用程式之裝置上的其他技術。
              </li>
              <li>
                帳戶資料：包括您的身份資料和密碼，您的購買記錄或訂單，您的興趣，偏好，回饋和問卷回覆。
              </li>
              <li>
                使用資料：包含有關您如何使用本應用程式和我們的服務的資料。
              </li>
              <li>
                行銷及宣傳資料：包括您在接收行銷宣傳（例如我們的提醒和活動通知）時的偏好。
              </li>
            </ol>
            <p>
              我們也蒐集及利用統計或人口數據等匯總資料。匯總資料可能從您的個人資料發展而成。
              但因該匯總資料即使與我們可能有權取得的任何其他資料相結合，仍無法從該匯總資料識別出您個人，所以匯總資料不被視為個資法定義的個人資料。舉例而言，
              我們可能將匯總您的使用資料，以計算使用特定應用程式功能用戶的百分比。
              不過如果我們將匯總資料與您的個人資料結合或連結，而能夠直接或間接識別您個人，我們會將此類結合的資料視為個人資料，並依本隱私權規範及聲明利用之。
            </p>

            <p>
              <b>如果您無法提供個人資料</b>
              <br />
              如果您被要求提供個人資料時未能提供，或您撤回您對提供個人資料的同意，我們可能無法與您訂定特定合約或完全履行我們之間的合約（例如為您提供我們全部或部分服務）。
              於此情形，我們可能須取消您訂購的服務並會通知您。如果法律要求我們蒐集您的個人資料，您不得拒絕提供該等個人資料。
            </p>
            <p>
              若您不願意提供各服務或交易所要求的相關個人資料予本公司，且不同意本公司就該等個人資料依法令規定、
              以及本隱私權規範及聲明或其他相關告知內容為相關之個人資料蒐集、處理、利用，本公司將尊重您的決定，惟承上所述，
              依照各該服務之性質或條件，您可能因此無法使用該等服務或完成相關交易，
              本公司並保留是否同意提供該等相關服務或完成相關交易之權利。
              倘若您提供個人資訊且持續使用本公司所提供之各種服務時，則將視為您確認同意本隱私權規範及聲明等相關內容。
            </p>
          </Section>

          {/* ---- 3 ---- */}
          <Section id="s3" title="如何蒐集您的個人資料">
            <p>我們使用不同的方法來蒐集您的資料，包括：</p>
            <p>
              直接互動。
              您可能透過與本應用程式互動、填寫表單或與我們溝通，而提供您的身份資料、聯絡資料、交易資料、使用資料及行銷及宣傳資料。此包含您在下列情況提供的個人資料：{" "}
            </p>
            <ol type="a">
              <li>在本應用程式建立帳戶</li>
              <li>申請本應用程式之會員</li>
              <li>透過本應用程式申請本活動</li>
              <li>將物品或文章標示為您的最愛</li>
              <li>在我們的商店辦理登記</li>
              <li>在我們的商店中出示 ID 條碼及/或優惠券</li>
              <li>將您的 ID 連接社群網路帳戶</li>
              <li>設定您接收我們行銷宣傳的偏好</li>
              <li>
                透過電子郵件、我們的即時聊天室，我們的電話客服或我們的零售商店給予我們意見或向我們提出諮詢。
                當您表示本應用程式發生問題時，我們可能會要求您協力提供資訊等確認身分或相應辦理，必要時亦可能酌收作業處理成本，如提供複本資料。
              </li>
            </ol>
            在您參與本活動的期間，我們可能會蒐集您的身份資料，尤其是您的語音和聲音，或包含您的影片和相片。
            <ol type="1">
              <li>
                <b>自動化技術或互動。</b>
                <br />
                當您與本應用程式互動時，我們可能會自動蒐集有關您設備、瀏覽行為和模式的技術資料和使用資料。我們使用應用程式邏輯、伺服器記錄檔
                和其他類似技術來蒐集這些個人資料。
                在大多數情況下，我們使用自動化決策的方式不會對您個人產生重大影響（舉例而言，我們對資料進行一般分析，以了解我們客戶的行為和特徵）。
              </li>

              <li>
                <b>第三方或公眾可得之來源。</b>
                <br />
                我們可能會從您已同意與我們分享您的資料的其他實體收到您的資料，例如從上述「第三方連結」段落所示外部電子商務網站取得您的購買資訊作為協力服務之一環。
              </li>

              <li>
                <b>使用 Cookies。</b> <br />
                為了提升本應用程式和本服務的品質及/或客製化用戶體驗，我們可能使用或讀取儲存在使用者或用戶「Cookies」資料來追蹤用戶於本應用程式上的行為。Cookies
                是儲存在您裝置上的小型文字檔案，可將您線上使用本應用程式與本服務的相關資訊提供給我們，以協助我們客製化您的用戶體驗，並使本應用程式更方便用戶使用。請注意，並非我們使用的所有Cookies
                都包含您的個人資料。「「Cookies」資料主要分為下列三項：「（1）
                功能 Cookie （Functional Cookies）
                ，主要用於記憶您在網站上的設定，以改善並提供您個人化的功能服務。「（2）
                效能 Cookie （Performance Cookies）
                ，透過紀錄使用者操作經驗，幫助我們改善網站的設計效能。透過此類
                Cookie，我們可以了解到網站的哪些頁面或服務功能是最多人使用，以及使用者可能遇到的系統錯誤，並進行修正。「（3）
                廣告行銷 Cookie （Advertising Cookies）
                ，為適當提供您最適當的本公司最新消息與產品資訊，透過此類
                cookie，可以讓我們與我們所合作廣告商，提供您感興趣的廣告內容。使您可以在本公司所經營或其他網站看到我們所提供的廣告內容。使用者可以經由瀏覽器的設定，取消、或限制此項功能，但可能因此無法使用部份網站功能。若您想知道如何取消、或限制此項功能，可與瀏覽器公司或本公司客服中心聯絡。
              </li>
            </ol>
          </Section>

          {/* ---- 4 ---- */}
          <Section id="s4" title="如何利用您的個人資料">
            <p>
              <b>蒐集使用處理您個人資料之目的與類別</b> <br />
              本公司為了提供電子商務服務、售後服務、履行法定或合約義務、保護當事人及相關利害關係人之權益、行銷、客戶管理與服務、以及經營合於營業登記項目或組織章程所定之業務等目的，
              依照各服務之性質，會蒐集您的姓名、連絡方式（包含但不限於電話、E-MAIL及地址等）、為了行銷目的所需之資料：
              教育程度、職業狀態、家庭婚姻狀況、經濟程度、為完成收款或付款所需之資料、ＩＰ位址、及其他得以直接或間接識別使用者身分之個人資料。
              此外，為提升服務品質， 本公司會依照所提供服務之性質，記錄使用者的
              IP
              位址、以及在本應用程式內的瀏覽活動（包括但不限於使用者所使用的軟硬體、所點選的網頁、下載的應用程式）
              等資料，而且這些資料僅將供作流量分析和網路行為調查，以便於改善本公司或本應用程式的服務品質，不會和特定個人相連繫。
            </p>
            <Typography sx={{ fontWeight: FONT.weightHead, mb: 1 }}>
              行動裝置 APP 等應用程式之權限
            </Typography>
            針對使用行動裝置 APP
            等應用程式之用戶或消費者，其所被蒐集的個人資料，
            將會處儲存於您的行動裝置上。行動裝置 APP
            等應用程式需要您賦予讀取儲存時得使用之權限如下（倘未來有其他更新權限時，將依最新內容為準）：
            <SimpleTable
              head={["權限", "用途"]}
              rows={[
                ["位置", "最近門市搜尋"],
                ["行事曆", "商品開賣通知"],
                ["相簿", "儲存訂單截圖"],
                ["聯絡人", "預購轉贈聯絡人功能"],
              ]}
            />
            <Typography sx={{ fontWeight: FONT.weightHead, mb: 1 }}>
              （iOS 或 Android）其他可能使用之權限
            </Typography>
            <SimpleTable
              head={["權限", "用途"]}
              rows={[
                ["取用 Google Play Service", "推播、Google Map"],
                ["接收推播", "接收推播"],
                ["震動", "收到推播時的震動"],
                ["執行網路呼叫", "一般網路傳輸功能"],
                ["取得精準位置", "門市位置查詢"],
                ["取得概略位置", "門市位置查詢"],
                ["寫入檔案", "APP 內部更新使用"],
                ["可執行安裝程式", "APP 內部更新使用"],
                ["取得 Account Service 帳號", "第三方行銷使用"],
                ["取得手機狀態", "第三方行銷使用"],
              ]}
            />
            <p>
              <b>法律依據</b>
              <br />
              以下說明我們利用您個人資料的所有方式。本公司是以您的同意為法律依據而使用您的個人資料。
            </p>
            <p>將您註冊為本應用程式的新用戶/會員；</p>
            <ul>
              <li>
                管理您的用戶∕會員帳戶，包含透過參考相連的帳戶資訊來協助您還原用戶∕會員帳戶
              </li>
              <li>
                管理我們與您的關係，包含通知您我們的使用條款或隱私權政策的變更、回應您的諮詢，及請求您留下評價
              </li>
              <li>
                為您提供我們的服務，包含發送文章、產品目錄、購物優惠、本活動訊息、提供購物優惠券、在我們的商店辦理登記、找尋附近商店、管理您的會員紅利點數、找回和
                搜尋您的購買記錄、發送調查以及協助本活動預訂和本活動管理；
              </li>
              <li>
                管理與保護我們的業務及本應用程式（包括疑難排解、資料分析、測試、系統維護、支援、報告及資料管理）
              </li>
              <li>
                提供相關內容與廣告給您，並評估或了解我們提供給您的廣告的有效性
              </li>
              <li>
                研究並使用資料分析來改善本應用程式、產品/服務、行銷、業務規劃、客戶關係與經驗
              </li>
              <li>就您可能感興趣的商品或服務向您提出建議和推薦</li>
            </ul>
            <p>
              <b>您的同意</b>
              <br />
              我們是以您的同意為法律依據而蒐集、處理和利用您的個人資料。在您安裝本應用程式時，我們會要求您同意我們依本隱私權規範及聲明蒐集、處理和利用您的個人資料。
            </p>
            <p>
              一旦您閱讀了本隱私權規範及聲明並開始使用本公司服務或本應用程式（包括但不限於透過註冊會員、預訂本活動、填寫表單、標記您最喜歡的文章、在我們的商店辦理登記、在我們的商店出示您的
              ID 條碼或優惠券等，自願向我們提供您的個人資料。）
              ，即視為您已同意我們依上述特定目的蒐集、處理和利用您的個人資料。
            </p>
            <p>
              您可以在本應用程式上設定您的偏好，包括開∕關接收活動優惠或提醒等，隨時選擇排除我們蒐集、處理或利用您個人資料的特定類別。
            </p>
            <p>
              <b>個人資料的利用</b>
              <br />
              本公司所蒐集的足以識別使用者身分的個人資料、消費與交易資料，
              或日後經您同意而提供之其他個人資料，
              都將供本公司於其公司內部、所合作第三方、依照蒐集之目的進行處理和利用、或為完成提供服務或履行合約義務之必要、
              或依照相關法令規定或有權主管機關（包括但不限於法院或台灣票據交換所）之命令或要求，
              否則本公司不會將足以識別使用者身分的個人資料提供給本隱私權規範及聲明未臚列的第三人（包括境內及境外）、或移作蒐集目的以外之使用。
              本公司會持續保管、處理及利用相關資料。在上開蒐集目的範圍內，本應用程式依據服務項目，
              可能會需要將所蒐集的個人資料，提供予合作廠商（包括但不限提供本應用程式服務之
              App、宅配貨運業者、通訊服務業者、
              金流服務業者與其他有輔助本公司履行合約義務或客戶服務必要之第三方廠商）以協助交易完成或終止。針對訊息通知，
              本公司可能會使用手機簡訊、EMAIL、各款 APP
              或應用程式通知型訊息、LINE 通知型訊息，
              通知出退貨與會員權益等必要資訊，即使未加入本公司／本應用程式服務之客戶，您仍可接收到通知型訊息，
              本公司∕本應用程式服務傳送之通知型訊息，以對您有效且重要的訊息為限，滿足以下三個條件者，
              將可收到通知型訊息（1）LINE
              帳號設定的電話號碼與本公司/本服務所傳來的電話號碼比對相符（2）該
              LINE 帳號已在 LINE APP 設定中， 同意接收通知型訊息（3）LINE
              帳號未封鎖傳送訊息之 LINE
              官方帳號。欲變更通知型訊息的設定，操作如下：
              「（1）點選「好友」＞「設定」（2）點選「隱私設定」
              （3）點選「提供使用資料」
              （4）點選「通知型訊息」（5）開關「接收通知型訊息」。關閉「接收通知型訊息」後，
              將不會接收到來自任何企業官方帳號或認證官方帳號的通知型訊息。惟，如使用者關閉「通知型訊息」而影響受通知者之自身權益或有損害時，將由使用者自行負責。
            </p>
            <p>
              若使用者選擇以信用卡方式支付，僅限信用卡持卡人本人使用，且得就此交易資料向發卡銀行、收單行及持卡人照會並確認是否屬實，於核對無誤後付款程序才算完成。
              依上開蒐集目的所取得的個人資料，可能會依據發卡銀行、收單行之隱私權規定傳送至該管單位進行保存或處理，且該管單位於符合個人資料保護法的情況下才會將您的資訊傳送至境外。
            </p>
            <p>
              <b>第三人利用與廣告（分析）服務或行銷</b>
              <br />
              為優化您的消費體驗，我們提供相關第三方認證服務的功能，第三方認證業者包含但不限於如
              Google、Facebook、Apple、Line 等，讓您可以利用此功能，
              更快速地完成本網站的註冊與登入流程。同時，為了讓您能更輕易地接觸所偏好的商品資訊，我們會在不同平台對您作優化廣告投放及使用流程的目的範圍內，
              請注意在您閱讀本規範及聲明、使用本公司服務或透過第三方認證服務下，如您持續使用本公司服務時，
              將理解為您同意的個人資料分享給提供廣告分析、投放的服務業者可能包含但不限於如
              Google、Facebook、Line、Yahoo、Criteo、Adobe、Tagtoo
              或新型態廣告分析業者，
              另該些服務業者得以為您進行精準的商品廣告安排，此外，我們也已安裝
              Google Analytics （分析） 廣告功能。
              如您欲停用此類功能及服務，建議您可參考 Google
              所提供不透露資訊的方式以停用該功能，或與我們聯繫，我們會提供必要的協助。
            </p>
            <p>
              我們只會在您事前同意的情況下，將您的個人資料分享給其他公司用於行銷或提供服務之目的。
              一旦經您要求或反對，我們將停止為行銷目的分享您的個人資料。
            </p>
            <p>
              除非獲得您的許可或依法律要求，否則我們不會將您的個人資料分享給第三人。
            </p>
            <p>
              <b>Google Analyics（分析）廣告功能</b>
              <br />
              本應用程式已安裝 Google Analytics （分析）
              廣告功能。本功能僅用於提供本公司分析廣告投放效益及優化使用流程，
              如您欲停用 Google Analytics（分析） 廣告功能，建議您可參考 Google
              所提供不透露資訊的方式 以停用該功能。
            </p>
            <p>
              <b>目的之變更</b>
              <br />
              我們只會將您的個人資料用於上述特定目的。
            </p>
            <p>
              如果我們需要將您的個人資料用於上述特定目的以外之其他目的，我們會通知您並向您說明法律依據。
            </p>
            <p>
              請注意，在法律要求或允許的情況下，我們可能會在您不知情或未取得您同意的情況下利用您的個人資料。
            </p>
          </Section>

          {/* ---- 5 ---- */}
          <Section id="s5" title="個人資料的揭露">
            <p>我們可能會依上述第 4 段所列目的，與下列各方分享您的個人資料。</p>
            <ul>
              <li>
                內部第三人：
                <br />
                本公司，包含代表自己或我們處理您個人資料而作為承包商提供我們資訊科技、系統管理、業務流程外包或其他服務。
              </li>
              <li>
                外部第三人：
                <br />
                為我們提供資訊科技、系統管理、網站流量測量、業務流程外包、分析或其他服務的服務提供承包商。舉例而言，我們與分析提供商（如Adobe
                Analytics）合作蒐集、處理或利用您的技術資料和使用資料，因此我們可能會從分析·
                提供商收到此類資料。
              </li>
              <li>
                代表自己或我們處理您個人資料，而為我們提供專業服務之專業顧問，包括律師、銀行、會計師和保險公司。
              </li>
              <li>
                在某些情況下要求我們報告處理行為的主管機關和其他有關當局。
              </li>
              <li>
                我們可能選擇出售、轉讓或與其合併部分業務或資產的第三人，或我們可能尋求收購其他業務或與其合併的第三人。如果我們的業務發生變化，則新的所有者得依與本隱私權規範及聲明規定之同樣方式利用您的個人資料。
              </li>
            </ul>
            <p>
              我們要求所有第三人尊重您個人資料的安全性，並依據法律、個資法和本隱私權規範及聲明對待您的個人資料。我們不允許第三人服務提供商將您的個人資料用於他們自己的目的，而只允許他們依第
              4 段中所列特定目的及我們的指示處理您的個人資料
            </p>
          </Section>

          {/* ---- 6 ---- */}
          <Section id="s6" title="國際傳輸">
            <p>
              您的個人資料將用於台灣，並透過外部服務提供商的伺服器提供儲存和處理和備援服務，本公司未參與外部雲端資訊科技資源，
              但委由外部服務服務提供商基於維護運營，可能將您的資料於日本和美國等地機房儲存和處理。本公司及其在台灣的承包商或外部服務提供商，
              受本國和他國個人資料保護法所定各種資料保護義務的拘束，並承諾依照個人資料保護法尊重您的隱私及保護您的個人資料。
              基此，本公司為您的個人資料提供與個人資料保護法相當的保護標準並約束承包商應遵守相關規定。
              於委由外部服務提供商維運之情形下，資料可能儲存或處理於日本、美國等地之資料
              中心；本公司及受託者均遵循各地個資法之保護義務。
            </p>
          </Section>

          {/* ---- 7 ---- */}
          <Section id="s7" title="資料安全">
            <p>
              本公司將以合於產業標準之合理技術及程序，採取適當的安全措施以防止您的個人資料意外遺失、未經授權遭使用或存取、修改或揭露，
              以維護本公司個人資料之安全。請留意，手機號碼是關聯您會員身分的核心認證資訊，若您手機遺失、更換手機號碼、或有變更手機號碼所有權名義時，
              請務必與本公司聯繫，進一步完成必要的個人資訊變更及確認，以確保您的個人資料、交易資料的安全性與機密性；若您未提出前述資訊變更需求及確認時，
              本公司基於無法確認本人下，可能無法提供進一步協助且使用者恐須自行負擔可能發生的一切損害。
            </p>
            <p>
              我們已採取適當的安全措施以防止您的個人資料意外遺失、未經授權遭使用或存取、修改或揭露。
              此外，我們只讓業務上需知悉您個人資料的員工、代理人、承包商及其他第三人存取您的個人資料。他們只會依照我們的指示處理您的個人資料並遵守保密義務。
            </p>
            <p>
              如果我們有相當理由認為個人資料遭侵犯，我們將根據個資法採取合理而迅速的方式處理。
            </p>
            <p>
              此處之「個人資料遭侵犯」是指
              （1）未經授權存取、蒐集、利用、揭露、複製、修改或處置個人資料；或
              （2）儲存個人資料的任何儲存媒介或設備有任何損失，而個人資料可能遭未經授權的存取、蒐集、利用、揭露、複製、修改或處置；或違反台灣個人資料保護法等相關規定。
            </p>
          </Section>

          {/* ---- 8 ---- */}
          <Section id="s8" title="資料保存">
            <p>
              除非法律要求或允許更長的保留期限，我們將於本隱私權規範及聲明第 4
              段所定特定目的之必要範圍內保留您的個人資料。
              在此保留期間內，我們會定期檢視您的個人資料，以便隨時更新並確保資料保留時間不會超過特定目的之必要範圍。
            </p>
            <p>
              <mark>
                依上開蒐集目的所取得的個人資料，本公司所委託的外部服務提供商，基於維護運營之目的，可能將資料保存於境外雲端服務供應商<i>Bluehost</i>
                之美國等地機房，本公司已依照個人資料保護法約束該管單位應符合之隱私權等相關規定，始由該管單位進行系統運營、保存或處理，
                且該管單位於符合個人資料保護法的情況下才會將您的資訊傳送至境外。
              </mark>
            </p>
            <p>
              在某些情況下，我們可能會將您的個人資料匿名化，例如將您的個人資料與其他資料匯總（使其不再與您個人產生連結），
              以用於研究或統計目的，或用於商業規劃。於此情形，我們在法律允許的範圍內可能會不另通知您而無限期使用這些資料。
            </p>
          </Section>

          {/* ---- 9 ---- */}
          <Section id="s9" title="您的法律權利">
            <p>
              資料當事人可以請求查詢、閱覽本人的個人資料或要求給予複本。惟，基於查詢、閱覽、要求複本等作業，本公司可能要求當事人負擔作業成本費用，
              包含但不限於如提供複本或其他作業之作業成本每筆或每項作業 30 元至
              300 元∕次不等。若您的個人資料有變更、或發現您的個人資料不正確，
              可以向本公司要求修改或更正；如您所提供之資料錯誤或缺漏，則不受本隱私權規範及聲明保護。您可要求刪除、停止處理或利用個人資料，
              但因本公司執行職務或業務所必須者，不在此限。另，資料當事人有協力作業或者配合申請之義務。因應歐盟個人資料保護法規
              （European Union General Data Protection
              Regulation,GDPR），歐盟國家使用者將暫停使用SEHO之各服務。若您有任何詢問，請與SEHO客服中心聯絡。
              若您欲行使刪除請求權，請直接聯繫專員或商店客服協助您行使權利，惟本公司得基於作業處理，酌收必要成本費用（如每筆或每項作業
              30 元至 300 元∕次不等） ， 並將處理結果以書面通知請求人。
            </p>
            <p>
              您依個資法有下列與個人資料相關的權利。請注意，您需經過身分確認及確保滿足法律要求之程序。如需此類程序以及如何行使您的權利的詳細資訊，請聯絡我們：
            </p>
            <p>
              <b>
                查詢、請求閱覽、請求製給複製本
                <br />
                您有權：
              </b>
            </p>
            <ol type="a">
              <li>請求查詢、閱覽您的個人資料或製給複製本</li>
              <li>請求提供本公司如何利用或揭露您個人資料的資訊</li>
            </ol>
            惟如有下列情況，因個資法或其他相關法律規定並不要求本公司提供上述資訊，本公司將依主管機關指示及內部審核決定是否提供：
            <ol type="a">
              <li>
                妨害國家安全、外交及軍事機密、整體經濟利益或其他國家重大利益
              </li>
              <li>妨害公務機關執行法定職務</li>
              <li>妨害本公司或第三人之重大利益</li>
            </ol>
            我們可能會收取合理的費用或必要處理成本來處理您的請求或提供服務。包含但不限於如提供複本或其他作業之作業成本每筆或每項作業
            30 元至 300 元∕次不等。 請求補充或更正
            <p>
              <b>請求補充或更正</b>
              <br />
              您有權請求我們補充或更正我們所擁有或控制的您的個人資料。
            </p>
            <p>
              <b>請求停止</b>
              <br />
              您有權請求停止我們蒐集、處理或利用您的個人資料。
              <br />
              此外，在下列情況下，我們會主動或依您的請求，刪除或停止收集、處理或利用您的個人資料，同時我們可能將因此限制、停止、或無法提供您服務：
            </p>
            <ol type="a">
              <li>
                您的個人資料正確性有爭議。但因執行職務或業務所必須，或經您書面同意，並經註明其爭議者，不在此限
              </li>
              <li>
                個人資料蒐集之特定目的消失或期限屆滿時。但因執行職務或業務所必須或經您書面同意者，不在此限
              </li>
              <li>蒐集、處理或利用個人資料違反個資法規定</li>
            </ol>
            <b>請求刪除</b>
            <br />
            您有權請求我們刪除您的個人資料。此外，在下列情況下，我們會主動或依您的請求刪除您的個人資料，同時我們可能將因此限制或停止、或無法提供您服務：
            <br />
            <br />
            個人資料蒐集之特定目的消失或期限屆滿時。但因執行職務或業務所必須或經您書面同意者，不在此限；或
            <br />
            蒐集、處理或利用個人資料違反個資法規定。
            <p>
              <b>撤回同意的權利</b>
              <br />
              在我們依據您的同意以蒐集、處理和利用您的個人資料之情形，您可以隨時撤回您的同意。但這不會影響您撤回同意前所為任何蒐集、處理或利用的合法性。如果您撤回同意，我們可能無法為您提供本應用程式中部分或全部的服務。如果有此種情形，我們會在您撤回同意時給您建議。
            </p>
            <p>
              <b>我們可能需要您提供之事項</b>
              <br />
              在我們回應任何請求之前，我們可能需要向您要求提供特定資訊以確認您的身份或協力相應辦理。此為安全措施以確保個人資料不會揭露給無權接收的人。我們也可能會與您聯絡並請您提供更多有關您的請求的資訊，以加快我們的回應速度。同時，我們得酌收合理處理成本。包含但不限於如提供複本或其他作業之作業成本每筆或每項作業
              30 元至 300 元∕次不等。
            </p>
          </Section>

          {/* ---- 10 ---- */}
          <Section id="s10" title="使用本活動之錄音/錄影和照片">
            <p>
              依據本隱私權規範及聲明的其他條款與條件，您同意我們可以將您參與本公司活動中所拍攝含有您的錄音/錄影及照片用於第
              4 段中所列特定目的，且不需向您支付任何費用。
              您也同意我們獨自擁有這些錄音、錄影和照片的著作權。
              如您不希望本活動中所拍攝含有您的錄音、錄影以及照片用於商業目的，則您應在參加本活動前向我們提出請求。但本公司可能因此婉拒您參與活動。
            </p>
          </Section>
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        anchor="top"
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            mt: `${topOffset}px`,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            maxHeight: `calc(100dvh - ${topOffset}px)`,
          },
        }}
      >
        {SideList}
      </Drawer>
    </Box>
  );
}
